import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";

import ApiAnalytics from "../../../../api/analytics";
import { useCan } from "../../../../common/utils/usePermissions";

import "./scenario-drop-off.css";

const PERIOD_OPTIONS = ["7d", "30d", "90d"];
const DEFAULT_PERIOD = "30d";

/**
 * Where scenario runs stop (LF360-1909).
 *
 * Moved here from Monitoring > Usage. Usage answers "is the app working for people" -
 * actives, screens, sessions, platform. Progress through a scenario is not that question,
 * and it belongs next to the rest of the scenario context.
 *
 * The move also settles a conflict. On Usage this block printed "Completed (61.6%)" as the
 * first number on the screen, which reads as a scenario completion rate - and programme
 * reporting already computes completion per scenario, with the full participant registry
 * behind it. App analytics only sees events that happened, so its figure is structurally
 * lower: it cannot see anyone who was assigned something and never opened it. Two places
 * showing a completion rate with different numbers is how a team stops trusting both.
 *
 * So there is no percentage here, deliberately. Every figure is an absolute count of runs,
 * including the stage-to-stage drop, which is the part programme reporting genuinely cannot
 * answer: not how many finished, but where inside a scenario people stopped.
 *
 * The data still comes from the usage endpoint - `funnel` is computed there and arrives in
 * the same response - so this component is a relocation, not a new query.
 *
 * `/v1/analytics/usage` needs `analytics.monitoring` (LF360-2024), the same permission as
 * the Scenarios page itself; the guard stays so the request is never issued for somebody
 * who would only get 403. Same boundary PreSignInActivity keeps on Onboarding.
 */
const ScenarioDropOff = () => {
  const { t } = useTranslation();
  const canMonitor = useCan("analytics.monitoring");

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canMonitor) return undefined;

    let isCurrent = true;
    setLoading(true);
    setFailed(false);

    ApiAnalytics.getUsageAnalytics(period)
      .then((response) => {
        if (!isCurrent) return;
        setData(response.data || null);
      })
      .catch((error) => {
        console.error("Load scenario drop-off error:", error);
        if (!isCurrent) return;
        setData(null);
        // No notification, for the same reason as PreSignInActivity: this is a secondary
        // panel introducing someone else's table. A failure here must not throw a toast
        // over the scenarios table, which is what the reader actually came for.
        setFailed(true);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [period, canMonitor]);

  const funnel = data?.funnel || { started: 0, answered: 0, completed: 0 };

  // The drop is carried as an absolute count of runs, never as a share of `started`.
  // `started` is also the bar scale: the widest bar is the entry stage by definition, so
  // the bars show the shape of the fall without any of them standing for a rate.
  const stages = useMemo(
    () => [
      {
        key: "started",
        label: t("analytics.scenario_drop_off.started"),
        count: funnel.started,
        drop: null,
        from: null,
      },
      {
        key: "answered",
        label: t("analytics.scenario_drop_off.answered"),
        count: funnel.answered,
        drop: Math.max(funnel.started - funnel.answered, 0),
        from: t("analytics.scenario_drop_off.started"),
      },
      {
        key: "completed",
        label: t("analytics.scenario_drop_off.completed"),
        count: funnel.completed,
        drop: Math.max(funnel.answered - funnel.completed, 0),
        from: t("analytics.scenario_drop_off.answered"),
      },
    ],
    [funnel.started, funnel.answered, funnel.completed, t]
  );

  const hasData = funnel.started > 0 || funnel.answered > 0 || funnel.completed > 0;

  if (!canMonitor) return null;

  return (
    <section className="scenario-drop-off">
      <div className="scenario-drop-off__head">
        <h3 className="scenario-drop-off__title">
          {t("analytics.scenario_drop_off.title")}
        </h3>
        <Select
          value={period}
          onChange={setPeriod}
          size="small"
          style={{ width: 140 }}
          options={PERIOD_OPTIONS.map((value) => ({
            value,
            label: t(`analytics.usage_dashboard.period.${value}`),
          }))}
        />
      </div>

      {/* LF360-1863. The unit is a scenario run, not a person. Said on the page rather
          than in a tooltip because the version before that one counted people, read
          ~100% at every stage and hid every abandonment - anyone familiar with it would
          otherwise carry that reading over. */}
      <p className="scenario-drop-off__note">
        {t("analytics.scenario_drop_off.note")}
      </p>

      {loading && !data ? (
        <p className="scenario-drop-off__empty">
          {t("analytics.usage_dashboard.loading")}
        </p>
      ) : failed ? (
        <p className="scenario-drop-off__empty">
          {t("analytics.usage_dashboard.load_error")}
        </p>
      ) : hasData ? (
        <div className="scenario-drop-off__stages">
          {stages.map((stage) => (
            <div className="scenario-drop-off__stage" key={stage.key}>
              <span className="scenario-drop-off__stage-label">{stage.label}</span>
              <span className="scenario-drop-off__track">
                <span
                  className="scenario-drop-off__bar"
                  style={{
                    width: `${
                      funnel.started ? (stage.count / funnel.started) * 100 : 0
                    }%`,
                  }}
                />
              </span>
              <span className="scenario-drop-off__stage-value">{stage.count}</span>
              <span className="scenario-drop-off__stage-drop">
                {stage.drop > 0
                  ? t("analytics.scenario_drop_off.drop_from", {
                      runs: stage.drop,
                      stage: stage.from,
                    })
                  : ""}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="scenario-drop-off__empty">{t("analytics.usage_dashboard.empty")}</p>
      )}
    </section>
  );
};

export default ScenarioDropOff;
